/*
 * <wc-hero-video>: the home hero's optional looping video (session W6,
 * 2026-10-08; Luke's L-34). The still under it is the hero and the LCP
 * element; this only ever adds motion on top, and only when it costs the
 * page nothing:
 *
 * - NO BYTES BEFORE THE PAGE IS USABLE. The <video> ships with no source and
 *   preload="none". The source is set after the load event, once the browser
 *   is idle, and only while the hero is on screen.
 * - NEVER under prefers-reduced-motion or Save-Data: the still stays. If
 *   reduced motion is switched on while it plays, it stops.
 * - IT APPEARS ONLY WHEN IT IS PLAYING (data-playing fades it in over the
 *   still), so a slow or failed load shows the still, never an empty frame.
 * - Off screen or in a hidden tab it pauses; it resumes on its return unless
 *   the visitor paused it.
 * - The pause button (a section setting, on by default) is shown only once
 *   the video plays, and says what it will do.
 *
 * The phone cut, when there is one, is chosen when the video first starts.
 */

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const phone = window.matchMedia("(max-width: 749px)");

class WcHeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");
    this.button = this.querySelector("[data-wc-hero-pause]");
    this.userPaused = false;
    if (!this.video || reduceMotion.matches || navigator.connection?.saveData) return;

    const idle = (fn) =>
      window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200);
    const start = () => idle(() => this.arm());
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });

    this.onMotion = () => {
      if (reduceMotion.matches) this.stop();
    };
    reduceMotion.addEventListener?.("change", this.onMotion);
  }

  disconnectedCallback() {
    this.seen?.disconnect();
    reduceMotion.removeEventListener?.("change", this.onMotion);
    document.removeEventListener("visibilitychange", this.onVisibility);
  }

  arm() {
    if (this.armed || reduceMotion.matches) return;
    const src = (phone.matches && this.dataset.srcMobile) || this.dataset.src;
    if (!src) return;
    this.armed = true;

    this.video.muted = true;
    this.video.addEventListener("playing", () => {
      this.dataset.playing = "";
      if (this.button) this.button.hidden = false;
    });
    this.video.addEventListener("error", () => this.stop(), { once: true });
    this.button?.addEventListener("click", () => this.toggle());

    this.onScreen = false;
    this.seen = new IntersectionObserver(
      ([entry]) => {
        this.onScreen = entry.isIntersecting;
        this.sync();
      },
      { threshold: 0.25 },
    );
    this.seen.observe(this);
    this.onVisibility = () => this.sync();
    document.addEventListener("visibilitychange", this.onVisibility);

    // Only now does anything download.
    this.video.preload = "auto";
    this.video.src = src;
  }

  sync() {
    if (!this.armed) return;
    if (this.onScreen && !document.hidden && !this.userPaused && !reduceMotion.matches) {
      const p = this.video.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      this.video.pause();
    }
  }

  toggle() {
    this.userPaused = !this.video.paused;
    if (this.userPaused) this.video.pause();
    else this.sync();
    const b = this.button;
    b.toggleAttribute("data-paused", this.userPaused);
    b.setAttribute("aria-label", this.userPaused ? b.dataset.labelPlay : b.dataset.labelPause);
  }

  stop() {
    this.video.pause();
    delete this.dataset.playing;
    if (this.button) this.button.hidden = true;
  }
}

if (!customElements.get("wc-hero-video")) customElements.define("wc-hero-video", WcHeroVideo);
